/**
 * Second Turn listings can hold several variants (colors, sizes, conditions)
 * on one PDP. Picks the variant to preselect: the first available variant in
 * the requested color, then any available variant, then the first variant.
 *
 * @param {Object} product - Product with `options` and `variants`
 * @param {string} [color] - Color to prefer (e.g. from the PLP card or ?color=)
 * @returns {Object|undefined}
 */
export const getSecondTurnDefaultVariant = (product, color) => {
  const { options = [], variants = [] } = product;
  const colorIndex = options.findIndex((option) => (option.name || option) === 'Color');
  const colorKey = `option${colorIndex + 1}`;
  const normalizedColor = color?.toLowerCase();

  const colorMatch =
    colorIndex > -1 && normalizedColor
      ? variants.find((variant) => variant.available && variant[colorKey]?.toLowerCase() === normalizedColor)
      : null;

  return colorMatch || variants.find((variant) => variant.available) || variants[0];
};

/**
 * Second Turn variants are one-offs, so most Color / Size / Condition combinations
 * don't exist. When a shopper picks an option value, jumps to the variant that has
 * it: an available one first, then the one that keeps the most current selections.
 * Picking a size or condition stays within the selected color when it can.
 *
 * @param {Object} product - Product with `options` and `variants`
 * @param {Object} selectedOptions - Current selections keyed by option name
 * @param {string} optionName - Option the shopper changed (e.g. "Size")
 * @param {string} value - Value the shopper picked
 * @returns {Object|undefined}
 */
export const getSecondTurnVariantForOption = (product, selectedOptions, optionName, value) => {
  const { options = [], variants = [] } = product;
  const optionNames = options.map((option) => option.name || option);
  const optionKey = (name) => `option${optionNames.indexOf(name) + 1}`;

  const withValue = variants.filter((variant) => variant[optionKey(optionName)] === value);
  const colorKey = optionNames.includes('Color') && optionName !== 'Color' ? optionKey('Color') : null;
  const sameColor = colorKey ? withValue.filter((variant) => variant[colorKey] === selectedOptions.Color) : [];
  const candidates = sameColor.length ? sameColor : withValue;

  const score = (variant) =>
    (variant.available ? optionNames.length : 0) +
    optionNames.filter((name) => variant[optionKey(name)] === selectedOptions[name]).length;

  return candidates.reduce((best, variant) => (!best || score(variant) > score(best) ? variant : best), undefined);
};
